import React, {
  lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Map as ImmutableMap } from 'immutable'
import { setNotificationsMenuVisible, setUserMenuVisible } from '../../../Header/actions'
import Button, { TYPES } from '../../../Button/index'
import CommunityErrorBoundary from '../../shared/ErrorHandling/CommunityErrorBoundary/index'
import { formatErrorForLogging, getLogger } from '../../../../log/index'
import lazyWithRetry from '../shared/lazyWithRetry'
import { readChatReadPosition } from '../shared/chatReadPositionStorage'
import { connect as connectStaticText } from '../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../StaticText/StaticTextContext'
import { usePostHogEvent } from '../../../PostHog/usePostHogEvent'
import type { CirclesDrawerOpenTrigger } from '../../../PostHog/events/Community/index'
import useCirclesEnabled from '../Gating/useCirclesEnabled'
// import type: erased at compile time, so the icon module still only loads via
// the lazy() below (the static import would crash SSR).
import type { CirclesSymbolVariant } from '../../../Icon/icons/CirclesSymbol/index'
import { useMyCircles, useMarkCircleReadOnClose } from '../../../../services/community/circles'
import {
  selectAuthJwt,
  selectAuthUid,
} from '../../../../services/community/identity/profile/redux/state-types'
import useCirclesAutoOpen from './useCirclesAutoOpen'
import useCircleDrawer from './useCircleDrawer'
import CirclesNavMenu, { CIRCLES_NAV_MENU_ID, useCirclesNavMenu } from '../CirclesNavMenu/index'
import type { NavPopoverCloseReason } from '../shared/NavPopover/index'

// While the drawer is open, lift the (now app-wide) sticky header's stacking
// context to the drawer's own z-index, above the page overlay band, so those
// overlays don't paint over the open drawer — see the
// `body__sticky-header.body__circles-drawer-open` rule in _CirclesNavEntry.scss
// for why z-17..99 page overlays demand it.
const DRAWER_OPEN_CLASS = 'body__circles-drawer-open'

/** The two legacy header menus' visibility flags live at `state.header`
 *  (Immutable Map; keys absent until first toggled — coerce with `!!`). */
interface HeaderMenusState {
  header?: ImmutableMap<string, unknown>
}

/**
 * Gated nav-bar entry for Circles. `useCirclesEnabled` (config killswitch AND
 * the `community-circles-beta` PostHog release flag) is the outer gate; inside
 * it, TWO disjoint audiences reach the same icon and it does different things
 * for each:
 *
 *  - MEMBERS (`circles.length > 0`) — the icon toggles the "My Circles" drawer.
 *    This is the original behaviour, unchanged.
 *  - The NO-CIRCLE COHORT — a viewer in zero circles who is in the
 *    `circles-beta-active-cohorts` flag gets the discovery dropdown
 *    (`CirclesNavMenu`) instead, offering circles to join. Everyone else in
 *    zero circles still sees nothing at all. Because that dropdown is the JOIN
 *    offer it is additionally DESKTOP-ONLY, hence the second `useCirclesEnabled`
 *    call below; the member/drawer path stays device-independent.
 *
 * All heavier circle data is loaded by the lazily-loaded drawer (live Brooklyn
 * GraphQL). Keep this entry data-light — no profile / user data beyond the
 * membership list.
 */

// Lazy so the drawer chunk only loads for allowlisted users, on first open.
const Circles = lazyWithRetry(() => import('../index'))
// Lazy-load the icon to avoid an SSR crash (the header is server-rendered).
const CirclesSymbolIcon = lazy(() => import('../../../Icon/icons/CirclesSymbol/index'))

// Hoisted: fully static, and as an inline prop it was the ONE thing defeating
// React.memo(CirclesNavMenu) — every entry re-render rebuilt the whole panel
// element tree (rendering-hoist-jsx / rerender-memo).
const MENU_SYMBOL = (
  <Suspense fallback={null}>
    <CirclesSymbolIcon size={30} alt="" variant="brand" />
  </Suspense>
)

/**
 * Monochrome icon variants for the header (the brand-teal artwork reads as a
 * random blue there): `white` on colored/transparent header states, `black` on
 * the solid white header. HeaderV2 owns the state → variant mapping. Derived
 * from the icon's own union (minus `brand`) so the two can't drift.
 */
export type CirclesNavIconVariant = Exclude<CirclesSymbolVariant, 'brand'>

interface CirclesNavEntryInnerProps {
  iconVariant?: CirclesNavIconVariant
}

export const CirclesNavEntryInner: React.FC<CirclesNavEntryInnerProps> = ({ iconVariant = 'black' }) => {
  const { enabled } = useCirclesEnabled()
  // The discovery menu is the JOIN offer, and joining is desktop-only — the same
  // rule `OnboardingFinal` applies to the other join surface, so the two agree.
  // Member surfaces (the drawer below) deliberately keep the plain, device-
  // independent gate. This is a UA check, not a viewport one: resizing never
  // toggles it, and "request desktop site" is a known accepted bypass.
  const { enabled: menuEnabled } = useCirclesEnabled({ requireDesktop: true })
  const triggerLabel = useTranslatedText({ keys: ['navTrigger', 'label'] })
  // The drawer is URL-as-state: `?circle=<uuid>` on the current path IS the open
  // state (see useCircleDrawer). `isOpen` and the selected circle both derive
  // from the URL, so a notification click, a hard load, and a shared link all
  // open it identically — from any page, repeatably. `sticky`: this is the
  // drawer's single mount point, so this instance (and only this one) keeps
  // the param alive across in-app navigation — the open drawer persists over
  // whatever page loads underneath; only an explicit close removes it.
  const {
    requestedCircleId, isOpen, openCircle, replaceCircle, closeCircle,
    requestedTab, consumeRequestedTab,
    requestedAction, consumeRequestedAction,
  } = useCircleDrawer({ sticky: true })
  const jwt = useSelector(selectAuthJwt)
  const uid = useSelector(selectAuthUid)
  const dispatch = useDispatch()
  const isLoggedIn = !!jwt
  // `poll` is passed HERE and nowhere else: this entry owns the unread/mention
  // badge, and every other useMyCircles consumer reads the same Apollo cache, so
  // a second timer would only duplicate requests. The hook additionally holds
  // the poll until `enabled && circles.length > 0` — the same condition as
  // `showEntry` below.
  const {
    circles, loading: circlesLoading, error: circlesError, refetch: refetchCircles,
  } = useMyCircles({
    jwt, isLoggedIn, enabled, poll: true,
  })
  // Membership is the per-user gate: config on + at least one joined circle.
  // While the query is in flight `circles` is empty, so members see the icon
  // appear after hydration + fetch (never a visible-then-hidden flash).
  const isMember = circles.length > 0
  // Membership-evidence stamp: observing membership `$set`s the PERMANENT
  // `circles_participant` person property (the flag's rejoin lane). Catches
  // members whose membership was created SERVER-SIDE (admin invite, MCP add)
  // — they never fire a client join event, and you can't leave a circle
  // without visiting as a member first, so this closes the leave-then-
  // stranded gap for every entry path. Double latch: the ref makes it
  // once-per-mount; the uid-scoped sessionStorage guard makes it
  // once-per-browser-session across hard reloads (nav entry mounts on every
  // page load — without the guard, every SSR load of every member would
  // re-fire). try/catch: private-mode storage throws; a failed guard just
  // re-fires the idempotent stamp.
  const trackMembershipObserved = usePostHogEvent('circlesMembershipObserved')
  const membershipStampedRef = useRef(false)
  useEffect(() => {
    if (!isMember || !uid || membershipStampedRef.current) return
    const guardKey = `circlesMembershipStamped:${uid}`
    try {
      if (window.sessionStorage.getItem(guardKey)) {
        membershipStampedRef.current = true
        return
      }
      window.sessionStorage.setItem(guardKey, '1')
    } catch {
      // Storage unavailable — fall through to the per-mount ref alone.
    }
    membershipStampedRef.current = true
    trackMembershipObserved()
  }, [isMember, uid, trackMembershipObserved])
  // The no-circle discovery menu. `showMenu` is false for members, so the two
  // audiences can never overlap; the hook owns the cohort-flag read and the
  // five-way ambiguity of an empty `circles` list.
  const {
    showMenu, options: navMenuOptions, joinedIds: navMenuJoinedIds, join: navMenuJoin,
    joinPending: navMenuJoinPending, noticeDismissed, dismissNotice, optOut,
  } = useCirclesNavMenu({
    enabled: menuEnabled,
    isLoggedIn,
    jwt,
    circleCount: circles.length,
    loading: circlesLoading,
    error: circlesError,
  })
  // A known-good nav-menu join, held as membership EVIDENCE for exactly the
  // feed it returned. Two consumers: `showEntry` below (a failed post-join
  // refetch sets `circlesError`, which turns `showMenu` off while `isMember`
  // is still false — without this term the whole entry, drawer included,
  // unmounts and the confirmed join shows the viewer nothing until some later
  // GET_MY_CIRCLES succeeds) and the isOpen-transition effect (which must
  // latch `hasOpened` so the drawer mounts; the drawer's own cache-and-network
  // membership read then recovers the list). STATE, not a ref — the render
  // gate has to re-evaluate when it lands. Cleared once real membership lands.
  const [justJoinedFeedId, setJustJoinedFeedId] = useState<string | null>(null)
  const showEntry = enabled && (isMember || showMenu || justJoinedFeedId != null)
  const [menuOpen, setMenuOpen] = useState(false)
  // Opting out hides the whole entry, so the open panel must not outlive it.
  useEffect(() => {
    if (!showMenu) setMenuOpen(false)
  }, [showMenu])
  // An OPEN drawer whose viewer is (settled, cleanly) in NO circle is a dead
  // open — leaving the last circle used to abruptly unmount the entry with
  // `?circle=<left-uuid>` still in the URL, which the sticky heal then
  // re-appended on EVERY navigation for the rest of the session (dead
  // deeplink in every shared URL, `isOpen` stuck true, close analytics and
  // mark-read never firing). Close it properly: `closeCircle` clears the
  // sticky memory and the param and drives the normal close side effects.
  // Guards: an in-flight or errored membership read is "unknown", never
  // "zero" (must not kill a legit deeplink open mid-load), and a just-joined
  // viewer's membership hasn't landed yet (`justJoinedFeedId` is the
  // membership EVIDENCE for that window). Also covers a deeplink to a circle
  // the viewer was removed from — the URL heals instead of polluting.
  useEffect(() => {
    if (!isOpen || isMember || circlesLoading || circlesError) return
    if (justJoinedFeedId != null) return
    closeCircle()
  }, [isOpen, isMember, circlesLoading, circlesError, justJoinedFeedId, closeCircle])
  // The audience-blind entry link (`?circles=open`, the alpha-email CTA — one
  // URL for every recipient): resolved per viewer once membership settles. A
  // MEMBER's click opens the drawer to their first circle (no per-recipient
  // uuid in the email; the resulting `?circle=` open is tagged 'deeplink' by
  // the trigger taxonomy, which is what it is). A zero-circle viewer whose
  // join menu is renderable gets it opened. Anyone else — mobile UA, outside
  // the cohort, opted out — is deliberately left alone: the param stays,
  // inert, and is consumed only when acted on, so eligibility arriving later
  // in the session (e.g. the offer query settling) still honors the click.
  // `actedRef` makes the action once-per-mount: StrictMode double-invocation
  // and the user closing what we opened must not re-fire it.
  const actedOnRequestedActionRef = useRef(false)
  useEffect(() => {
    if (requestedAction !== 'open' || actedOnRequestedActionRef.current) return
    if (circlesLoading || circlesError) return
    if (isOpen) {
      // Already open (a URL carrying both params) — the action is moot.
      actedOnRequestedActionRef.current = true
      consumeRequestedAction()
      return
    }
    if (isMember) {
      actedOnRequestedActionRef.current = true
      // The open itself consumes the action: openCircle strips the param from
      // the URL it builds (see useCircleDrawer) — one navigation, no
      // consume-then-open race.
      openCircle(circles[0].id)
      return
    }
    if (showMenu) {
      actedOnRequestedActionRef.current = true
      consumeRequestedAction()
      setMenuOpen(true)
    }
  }, [
    requestedAction, circlesLoading, circlesError, isOpen, isMember, circles,
    showMenu, consumeRequestedAction, openCircle,
  ])
  // Reverse edge of the mutual-exclusion contract: the legacy menus know
  // nothing about this one, and their outside-dismissal is pointerdown-based —
  // a keyboard Enter on the bell (a click, never a pointerdown) would stack
  // its panel over the open circles menu. Boolean-coerced INSIDE the selector
  // (AppDownloadBanner precedent) so unrelated header dispatches never
  // re-render the entry.
  const otherHeaderMenuOpen = useSelector((state: HeaderMenusState) => {
    const header = ImmutableMap.isMap(state.header) ? state.header : undefined
    return !!(
      header
      && (header.get('userMenuVisible') || header.get('notificationsMenuVisible'))
    )
  })
  useEffect(() => {
    if (otherHeaderMenuOpen) setMenuOpen(false)
  }, [otherHeaderMenuOpen])
  const totalUnread = useMemo(
    () => circles.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    [circles],
  )
  // Mentions are the loud signal (Discord model): when any unread message @-mentions
  // the viewer, the badge turns into a red mention count; otherwise it's the gray
  // unread count. Both clear via the same markCommunityFeedRead + drawer-close refetch.
  const totalMentions = useMemo(
    () => circles.reduce((sum, c) => sum + (c.unreadMentionCount ?? 0), 0),
    [circles],
  )
  const mentionBadgeLabel = useTranslatedText({
    keys: ['navTrigger', 'mentionBadge'],
    params: { count: String(totalMentions) },
  })
  // Mount the drawer only after the first open (keeps its chunk off the wire
  // for users who never click) but keep it mounted afterward so `AppDrawer`
  // can animate close.
  const [hasOpened, setHasOpened] = useState(false)

  // The app header is sticky for everyone now (mounted in `HeaderApp` via
  // `useStickyHeader`), so the drawer's `--app-drawer-top` offset var is always
  // published — no cohort pin needed here.

  useEffect(() => {
    // isMember: in discovery-menu mode a `?circle=` param can set isOpen with
    // no drawer mounted (see the transition effect below) — the header z-index
    // lift must not fire for a drawer that isn't there.
    if (!isOpen || !isMember) return undefined
    document.body.classList.add(DRAWER_OPEN_CLASS)
    return () => document.body.classList.remove(DRAWER_OPEN_CLASS)
  }, [isOpen, isMember])

  const trackDrawerOpened = usePostHogEvent('circlesDrawerOpened')
  const trackDrawerClosed = usePostHogEvent('circlesDrawerClosed')
  const trackNavMenuViewed = usePostHogEvent('circlesNavMenuViewed')

  // The nav icon toggles the drawer: open to the first joined circle
  // (single-circle MVP), or close if already open. Both navigate the URL
  // (useCircleDrawer); the open/close SIDE EFFECTS are driven off the resulting
  // isOpen transition below, so every open path — icon, notification, onboarding,
  // hard-load deeplink — is tracked once, in one place, and each reports its own
  // `trigger` rather than collapsing into `manual`.
  // null = "nothing in this app initiated an open", which is precisely the
  // definition of a deeplink (hard load / notification / shared link /
  // back-forward already carrying `?circle=`). Only the two local entry points
  // below claim a trigger; the transition effect resolves the rest.
  const pendingOpenTriggerRef = useRef<CirclesDrawerOpenTrigger | null>(null)
  const handleTriggerClick = useCallback(() => {
    // Discovery-menu mode: the viewer has no circle to open, so the icon toggles
    // the dropdown instead. Opening it closes the other two nav popups, matching
    // the mutual-exclusion contract NotificationsMenu and MenuUser hold with
    // each other (otherwise three panels stack).
    if (showMenu) {
      // Side effects OUTSIDE the state updater (an updater must be pure —
      // StrictMode/concurrent can double-invoke it; same rule as setDock in
      // useDrawerLayout). Opening closes the other two nav popups, matching
      // the mutual-exclusion contract NotificationsMenu and MenuUser hold
      // with each other (otherwise three panels stack).
      const next = !menuOpen
      if (next) {
        dispatch(setNotificationsMenuVisible(false))
        dispatch(setUserMenuVisible(false))
        // The offer→join funnel's denominator: one impression per OPEN (the
        // join is circles_member_joined; opt-out/notice events live with the
        // menu hook). Keys are rainbow-category content refs, never PII.
        trackNavMenuViewed(navMenuOptions.map((o) => o.rainbowCategoryKey))
      }
      setMenuOpen(next)
      return
    }
    if (isOpen) {
      closeCircle()
      return
    }
    const firstCircleId = circles[0]?.id
    if (firstCircleId) {
      pendingOpenTriggerRef.current = 'manual'
      openCircle(firstCircleId)
    }
  }, [
    showMenu, menuOpen, dispatch, isOpen, closeCircle, openCircle, circles,
    trackNavMenuViewed, navMenuOptions,
  ])

  // The trigger Button (forwardRef) — the Escape-close focus-restore target.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeMenu = useCallback((reason?: NavPopoverCloseReason) => {
    setMenuOpen(false)
    // Dialog contract half 2 (half 1 = NavPopover focusing its panel on open):
    // a KEYBOARD close restores focus to the trigger, or the keyboard user is
    // dropped to <body> and Tab restarts from the top of the page. An
    // outside-click close deliberately does NOT restore — the click already
    // placed focus. Opt-out unmounts the whole entry, so it has no stable
    // restore target (accepted residual, documented in the review).
    if (reason === 'escape') triggerRef.current?.focus()
  }, [])
  const trackMemberJoined = usePostHogEvent('circlesMemberJoined')
  const handleMenuJoin = useCallback((rainbowCategoryKey: number) => {
    // assignToCircle is pool-wide idempotent and capacity-enforced; it returns
    // the joined feed. On success open the drawer to it — the same hand-off
    // onboarding uses — and let the membership refetch flip this entry back to
    // its normal drawer role. On failure the hook resolves null and the menu
    // stays open (Community rule 8: stay in the current state).
    navMenuJoin(rainbowCategoryKey)
      .then(async (feedId) => {
        // A null here is either a real join failure (the hook toasts "Circle is
        // full" itself — it is the only layer that can tell a failure from its
        // own single-flight suppression) or a suppressed duplicate. Either way
        // the menu stays open, per Community rule 8.
        if (!feedId) return
        trackMemberJoined(feedId)
        setMenuOpen(false)
        // Pre-warm membership BEFORE opening — the same reason the onboarding
        // join awaits a network-only GET_MY_CIRCLES before redirecting: opening
        // against the cached empty list can race the drawer's own queries and
        // flash its "no circles" empty state until the refetch lands. A failed
        // refetch still opens via the justJoined latch (see its declaration):
        // the entry stays mounted, the transition effect accepts this feed id
        // as membership evidence, and the mounted drawer's own
        // cache-and-network GET_MY_CIRCLES read recovers the list.
        setJustJoinedFeedId(feedId)
        await refetchCircles().catch(() => {})
        // 'auto' — the app-initiated post-join hand-off, same taxonomy as the
        // onboarding hand-off below ('manual' is reserved for a nav-icon click).
        pendingOpenTriggerRef.current = 'auto'
        openCircle(feedId)
      })
      .catch(() => {})
  }, [navMenuJoin, trackMemberJoined, openCircle, refetchCircles])

  // Track the circle currently open so we can mark IT read on close (the URL
  // param — and thus requestedCircleId — is already null by the close transition).
  const markCircleRead = useMarkCircleReadOnClose(jwt)
  const lastOpenCircleIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (requestedCircleId) lastOpenCircleIdRef.current = requestedCircleId
  }, [requestedCircleId])

  // Side effects on the URL-derived isOpen transition: lazy-mount the drawer on
  // first open, track open/close, and on close mark the circle read (the chat
  // poll's mark-read is throttled to 15s and can miss the last messages seen),
  // THEN refetch so the nav badge reflects the fresh, zeroed counts.
  const prevIsOpenRef = useRef(false)
  useEffect(() => {
    // MEMBERSHIP-gated: in discovery-menu mode a `?circle=` param (a former
    // member's stale @mention link) would otherwise mount the drawer empty for
    // a NON-member — firing deeplink analytics, GET_DISCOVER_CIRCLES, and a
    // close-time mark-read on a feed the viewer doesn't belong to. "A
    // non-member never reaches the drawer" (Circles/CLAUDE.md) is the
    // structural invariant CirclesDrawer's `enabled: true` rests on, so
    // `hasOpened` must never latch without membership. While GET_MY_CIRCLES is
    // in flight isMember is false; the effect re-runs on the flip, so a real
    // member's deeplink opens one run later (same latch pattern as
    // useCirclesAutoOpen below). NOTE: the drawer JSX stays keyed on
    // `hasOpened` alone — once latched, a transient empty `circles` blip must
    // not unmount an open drawer (pause-don't-unmount).
    // A just-completed nav-menu join counts as membership for exactly the
    // circle it returned (see justJoinedFeedId's declaration): the server
    // confirmed the join even if the local refetch failed, so the drawer must
    // still mount. lastOpenCircleIdRef is current here — its tracking effect
    // above runs first in the same commit.
    const justJoined = justJoinedFeedId != null
      && justJoinedFeedId === lastOpenCircleIdRef.current
    if (isMember && justJoinedFeedId != null) setJustJoinedFeedId(null)
    if (!isMember && !justJoined) {
      prevIsOpenRef.current = false
      return
    }
    if (isOpen && !prevIsOpenRef.current) {
      setHasOpened(true)
      const trigger = pendingOpenTriggerRef.current ?? 'deeplink'
      trackDrawerOpened(trigger)
      pendingOpenTriggerRef.current = null
    } else if (!isOpen && prevIsOpenRef.current) {
      // If this branch fires on an ordinary in-app navigation, the sticky
      // URL heal is leaking a transient closed state again — that is the bug
      // `useCircleDrawer`'s `effectiveCircleId` exists to prevent, and it also
      // costs a mark-read + a membership refetch per navigation.
      trackDrawerClosed()
      const closedCircleId = lastOpenCircleIdRef.current
      // Flush the visible-scroll position the chat-side 15s throttle may have
      // stranded. Deliberately the STORED position, not "now": closing the
      // drawer is not evidence the viewer reached the newest message, and
      // marking everything read here would undo the whole point of the
      // visible-scroll model. No position (never scrolled a bubble) ⇒ no call.
      const position = closedCircleId && uid
        ? readChatReadPosition(String(uid), closedCircleId)
        : null
      const marked = closedCircleId && position
        ? markCircleRead(closedCircleId, position)
        : Promise.resolve()
      marked.then(() => refetchCircles()).catch(() => {})
    }
    prevIsOpenRef.current = isOpen
  }, [
    isOpen, isMember, uid, justJoinedFeedId,
    trackDrawerOpened, trackDrawerClosed, refetchCircles, markCircleRead,
  ])

  // The drawer boundary renders null on error — without this log the drawer
  // "just doesn't open" with zero signal anywhere.
  const handleDrawerError = useCallback((error: Error, info: React.ErrorInfo) => {
    getLogger()?.error({
      ...formatErrorForLogging(error, 'Circles drawer render error'),
      // Deliberately NO `err` key: with it this record would reach
      // newrelic.noticeError and feed the pager-attached JS-error metric.
      // The boundary's CommunityErrorBoundary PageAction is this render
      // error's NR sink; this log is local-only.
      componentStack: info.componentStack ?? undefined,
    })
  }, [])

  // Onboarding hand-off: after the user joins a circle on the final onboarding
  // screen, a sessionStorage signal is stashed; when the gated nav entry mounts,
  // navigate to the joined circle (first joined, single-circle MVP), tagged as an
  // 'auto' open. Single-fire + uid-scoping live in the hook.
  const handleAutoOpen = useCallback(() => {
    const firstCircleId = circles[0]?.id
    if (firstCircleId) {
      pendingOpenTriggerRef.current = 'auto'
      openCircle(firstCircleId)
    }
  }, [circles, openCircle])
  // Gated on MEMBERSHIP, not `showEntry`: the entry can now also be visible in
  // discovery-menu mode, where there is no circle to auto-open and consuming the
  // one-shot sessionStorage signal would silently burn the hand-off. The
  // membership condition is false while GET_MY_CIRCLES is in flight; the hook
  // leaves the signal untouched when disabled and re-arms on the flip (deps
  // include `enabled`), so the hand-off survives the brief membership load.
  useCirclesAutoOpen(enabled && isMember, uid, handleAutoOpen)

  // The notification deep link needs no effect here: a circle @mention links to
  // `?circle=<feedUuid>` on the current path, and useCircleDrawer reads that param
  // reactively — so isOpen + the selected circle simply follow the URL, opening
  // the drawer wherever the click happened, repeatably.

  return showEntry ? (
    <div className="circles-nav-entry">
      <Button
        ref={triggerRef}
        type={TYPES.ICON}
        className="circles-nav-entry__trigger"
        onClick={handleTriggerClick}
        title={triggerLabel}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={showMenu ? menuOpen : isOpen}
        aria-controls={showMenu && menuOpen ? CIRCLES_NAV_MENU_ID : undefined}
        iconSvg={(
          <Suspense fallback={null}>
            <CirclesSymbolIcon size={24} alt="" variant={iconVariant} />
          </Suspense>
        )}
      />
      {showMenu ? (
        <CirclesNavMenu
          isOpen={menuOpen}
          onClose={closeMenu}
          options={navMenuOptions}
          joinedIds={navMenuJoinedIds}
          onJoin={handleMenuJoin}
          joinPending={navMenuJoinPending}
          noticeDismissed={noticeDismissed}
          onDismissNotice={dismissNotice}
          onOptOut={optOut}
          symbol={MENU_SYMBOL}
        />
      ) : null}
      {(totalUnread > 0 || totalMentions > 0) && !isOpen ? (
        <span
          className={`circles-nav-entry__badge${totalMentions > 0 ? ' circles-nav-entry__badge--mention' : ''}`}
          {...(totalMentions > 0 ? { 'aria-label': mentionBadgeLabel } : {})}
        >
          {(() => {
            const count = totalMentions > 0 ? totalMentions : totalUnread
            return count > 99 ? '99+' : count
          })()}
        </span>
      ) : null}
      {hasOpened ? (
        // A drawer failure degrades to the icon-only nav entry (fallback
        // renders null) instead of bubbling to the app-root boundary and
        // unmounting the whole app. The failure must not be silent or
        // permanent, though: onError logs it (this boundary swallows ANY
        // render error in the drawer tree, not just chunk loads), and
        // resetKeys on isOpen re-arms the boundary so closing and reopening
        // the drawer retries instead of dying until a full page reload.
        <CommunityErrorBoundary
          source="CirclesNavEntry.drawer"
          fallback={null}
          onError={handleDrawerError}
          resetKeys={[isOpen]}
        >
          <Suspense fallback={null}>
            <Circles
              isOpen={isOpen}
              onClose={closeCircle}
              requestedCircleId={requestedCircleId}
              replaceCircle={replaceCircle}
              requestedTab={requestedTab}
              consumeRequestedTab={consumeRequestedTab}
            />
          </Suspense>
        </CommunityErrorBoundary>
      ) : null}
    </div>
  ) : null
}

interface CirclesNavEntryProps {
  staticText: InternationalStrings
  iconVariant?: CirclesNavIconVariant
}

/**
 * Boundary: injects the `communityCircles` static-text slice so the trigger's
 * aria-label resolves, then renders the gated trigger + drawer. Reuses the same
 * slice the drawer connects to (no new storeKey).
 */
const CirclesNavEntry: React.FC<CirclesNavEntryProps> = ({ staticText, iconVariant }) => (
  <StaticTextProvider staticText={staticText}>
    <CirclesNavEntryInner iconVariant={iconVariant} />
  </StaticTextProvider>
)

export default connectStaticText({ storeKey: 'communityCircles' })(CirclesNavEntry)
